/**
 * Card component for displaying recent activity items (quotes, invoices, etc.)
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface RecentActivityCardProps {
  title: string;
  subtitle?: string;
  badge?: {
    label: string;
    variant?: "default" | "secondary" | "outline";
    className?: string;
  };
  metadata?: Array<{
    label: string;
    value: string | number;
  }>;
  footer?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  testId?: string;
}

export function RecentActivityCard({
  title,
  subtitle,
  badge,
  metadata,
  footer,
  onClick,
  className,
  testId,
}: RecentActivityCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors border",
        className
      )}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">{title}</div>
          {badge && (
            <Badge variant={badge.variant || "secondary"} className={cn("text-xs", badge.className)}>
              {badge.label}
            </Badge>
          )}
        </div>
      </div>
      {subtitle && (
        <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
      )}
      {metadata && metadata.length > 0 && (
        <div className={cn(
          "flex flex-col gap-1 mt-2 pt-2 border-t text-xs",
          metadata.length === 1 && "flex-row items-center justify-between"
        )}>
          {metadata.map((item, index) => (
            <div
              key={index}
              className={cn(
                metadata.length > 1 && "flex items-center justify-between"
              )}
            >
              <span className="text-muted-foreground">{item.label}:</span>
              <span className={cn(
                "font-medium",
                item.label.toLowerCase().includes("outstanding") && "font-semibold text-orange-600"
              )}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {footer && <div className="mt-2 pt-2 border-t">{footer}</div>}
    </div>
  );
}

